import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Copy, Download, KeyRound, Pencil, Plus, RefreshCw, Server, Trash2, Webhook } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { SEVERITIES } from '@/components/monitoring';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field, PageHeader } from '@/components/ui/form-field';
import { Alert, Badge, Card, CardContent, CardHeader, CardTitle, Input, Select, Skeleton, Switch } from '@/components/ui/primitives';
import { api, errorMessage } from '@/lib/api';
import type { TicketMeta, WebhookSetup, ZabbixInstance } from '@/lib/types';
import { formatDateTime, formatRelative } from '@/lib/utils';

const KEY = ['integrations', 'zabbix'];
export const KIND_LABEL = { ZABBIX: 'Zabbix', NETDOC: 'NetDoc' } as const;
const onError = (e: unknown) => toast.error(errorMessage(e));
const show = (r: { ok: boolean; message: string }) => (r.ok ? toast.success(r.message) : toast.error(r.message, { duration: 12_000 }));

function copy(value: string, label: string) {
  navigator.clipboard.writeText(value).then(
    () => toast.success(`${label} kopiert`),
    () => toast.error('Kopieren nicht möglich'),
  );
}

function CopyRow({ label, value, secret }: { label: string; value: string; secret?: boolean }) {
  return (
    <div className="grid gap-1">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <div className="flex items-center gap-2">
        <code className={`min-w-0 flex-1 truncate rounded-md border px-2 py-1.5 text-xs ${secret ? 'border-amber-500/50 bg-amber-500/10' : 'bg-muted/50'}`}>{value}</code>
        <Button type="button" variant="outline" size="icon" className="size-8 shrink-0" aria-label={`${label} kopieren`} onClick={() => copy(value, label)}>
          <Copy />
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

type Form = Omit<
  ZabbixInstance,
  'id' | 'status' | 'version' | 'lastError' | 'lastCheckedAt' | 'lastHostSyncAt' | 'lastPollAt' | 'lastWebhookAt' | 'hasWebhookSecret' | 'hasPortalToken' | 'portalTokenCreatedAt' | 'portalTokenUsedAt' | 'hostCount' | 'openProblems'
> & {
  apiToken: string;
};

const empty: Form = {
  kind: 'ZABBIX',
  writeExternalIds: false,
  name: '',
  url: '',
  apiToken: '',
  verifyTls: true,
  pollEnabled: true,
  active: true,
  autoTicket: true,
  minSeverity: 3,
  areaId: null,
  teamId: null,
  assigneeId: null,
  requireCustomer: true,
  requireContract: false,
  resolveOnRecovery: false,
  allowAcknowledge: false,
  reportTickets: true,
  resolveInSource: false,
};

/** NetDoc kennt drei Schweregrade – abgebildet auf die Zabbix-Skala der Ticketregel. */
const NETDOC_SEVERITIES = [
  { value: 1, label: 'Info' },
  { value: 2, label: 'Warnung' },
  { value: 4, label: 'Kritisch' },
];
const severityLabel = (kind: ZabbixInstance['kind'], v: number) =>
  kind === 'NETDOC' ? (NETDOC_SEVERITIES.find((s) => s.value >= v)?.label ?? 'Kritisch') : SEVERITIES[v]?.label;

function Toggle({ checked, onChange, label, hint }: { checked: boolean; onChange: (v: boolean) => void; label: string; hint: string }) {
  return (
    <label className="flex items-start gap-3 rounded-md border p-3 text-sm">
      <Switch checked={checked} onCheckedChange={onChange} className="mt-0.5" />
      <span>
        <span className="font-medium">{label}</span>
        <span className="block text-muted-foreground">{hint}</span>
      </span>
    </label>
  );
}

function InstanceDialog({ edit, onClose, onCreated }: { edit: { id: string | null; form: Form } | null; onClose: () => void; onCreated: (id: string, secret: string) => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState<Form>(empty);
  useEffect(() => {
    if (edit) setForm(edit.form);
  }, [edit]);
  const { data: meta } = useQuery({ queryKey: ['tickets', 'meta'], queryFn: () => api<TicketMeta>('/tickets/meta') });
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));
  const save = useMutation({
    mutationFn: (): Promise<ZabbixInstance | { instance: ZabbixInstance; webhookSecret: string | null }> => {
      const body = { ...form, apiToken: form.apiToken.trim() || undefined };
      return edit?.id
        ? api<ZabbixInstance>(`/integrations/zabbix/${edit.id}`, { method: 'PUT', body })
        : api<{ instance: ZabbixInstance; webhookSecret: string }>('/integrations/zabbix', { method: 'POST', body });
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: ['monitoring'] });
      toast.success('Monitoring-Quelle gespeichert');
      onClose();
      if ('webhookSecret' in r && r.webhookSecret) onCreated(r.instance.id, r.webhookSecret);
    },
    onError,
  });

  return (
    <Dialog open={edit !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent wide>
        <form
          className="flex min-h-0 flex-col"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>{edit?.id ? `${KIND_LABEL[form.kind]} bearbeiten` : 'Monitoring-Quelle verbinden'}</DialogTitle>
            <DialogDescription>
              {form.kind === 'ZABBIX' ? (
                <>
                  API-Token in Zabbix unter <em>Benutzer → API-Token</em> für einen Benutzer mit Leserechten auf die gewünschten Hostgruppen erzeugen (für Bestätigungen zusätzlich das Recht, Probleme zu bestätigen).
                </>
              ) : (
                <>
                  API-Token in NetDoc unter <em>Einstellungen › API-Tokens</em> mit Rolle <code>editor</code> anlegen (Ticketnummern zurückschreiben, bestätigen, Kundennummern eintragen; <code>viewer</code> genügt nur zum Lesen). Nach dem Speichern zeigt das Portal Adresse und Geheimnis für den Webhook-Empfänger in NetDoc.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="grid gap-5">
            {!edit?.id && (
              <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Art der Quelle">
                {(['ZABBIX', 'NETDOC'] as const).map((k) => (
                  <button
                    key={k}
                    type="button"
                    role="radio"
                    aria-checked={form.kind === k}
                    onClick={() => setForm((f) => ({ ...f, kind: k, pollEnabled: k === 'NETDOC' ? true : f.pollEnabled, minSeverity: k === 'NETDOC' ? 4 : 3 }))}
                    className={`rounded-lg border p-3 text-left text-sm transition-colors ${form.kind === k ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'hover:bg-muted'}`}
                  >
                    <span className="block font-medium">{KIND_LABEL[k]}</span>
                    <span className="text-muted-foreground">{k === 'ZABBIX' ? 'Monitoring: Hosts, Hostgruppen, Probleme (Webhook und Abgleich)' : 'RMM: Vorgänge per Webhook (sofort), Abgleich jede Minute, Ticketnummer zurück'}</span>
                  </button>
                ))}
              </div>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Name" htmlFor="z-name">
                <Input id="z-name" required value={form.name} onChange={(e) => set('name', e.target.value)} placeholder={form.kind === 'ZABBIX' ? 'z. B. Zabbix Rechenzentrum' : 'z. B. NetDoc'} />
              </Field>
              <Field label={`${KIND_LABEL[form.kind]}-URL`} htmlFor="z-url" hint={form.kind === 'ZABBIX' ? 'Adresse des Zabbix-Frontends' : 'Adresse der NetDoc-Weboberfläche'}>
                <Input id="z-url" required type="url" value={form.url} onChange={(e) => set('url', e.target.value)} placeholder={form.kind === 'ZABBIX' ? 'https://zabbix.example.de' : 'https://netdoc.example.de'} />
              </Field>
              <Field label="API-Token" htmlFor="z-token" hint={edit?.id ? 'Gespeichert. Nur zum Erneuern ausfüllen.' : undefined} className="sm:col-span-2">
                <Input
                  id="z-token"
                  type="password"
                  autoComplete="new-password"
                  required={!edit?.id}
                  value={form.apiToken}
                  onChange={(e) => set('apiToken', e.target.value)}
                  placeholder={edit?.id ? '•••••••••••• (unverändert)' : ''}
                />
              </Field>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <Toggle checked={form.active} onChange={(v) => set('active', v)} label="Aktiv" hint="Webhooks annehmen und abgleichen" />
              {form.kind === 'ZABBIX' ? (
                <Toggle checked={form.pollEnabled} onChange={(v) => set('pollEnabled', v)} label="Minütlich abgleichen" hint="Fängt verlorene Webhooks ab" />
              ) : (
                <Toggle checked={form.writeExternalIds} onChange={(v) => set('writeExternalIds', v)} label="Kundennummern eintragen" hint="Zuordnung im Portal als external_id in NetDoc speichern" />
              )}
              <Toggle checked={form.verifyTls} onChange={(v) => set('verifyTls', v)} label="Zertifikat prüfen" hint="Nur bei selbstsignierten Zertifikaten abschalten" />
            </div>

            <fieldset className="grid gap-4 rounded-lg border p-4">
              <legend className="px-1 text-sm font-medium">Ticketregel</legend>
              <Toggle checked={form.autoTicket} onChange={(v) => set('autoTicket', v)} label="Tickets automatisch erstellen" hint="Bei neuen Problemen, die die Bedingungen unten erfüllen" />
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Ab Schweregrad" htmlFor="z-sev">
                  <Select id="z-sev" value={form.minSeverity} onChange={(e) => set('minSeverity', Number(e.target.value))}>
                    {form.kind === 'NETDOC'
                      ? NETDOC_SEVERITIES.map((s) => (
                          <option key={s.value} value={s.value}>
                            {s.label}
                          </option>
                        ))
                      : SEVERITIES.map((s, i) => (
                          <option key={i} value={i}>
                            {s.label}
                          </option>
                        ))}
                  </Select>
                </Field>
                <Field label="Ticketbereich" htmlFor="z-area">
                  <Select id="z-area" value={form.areaId ?? ''} onChange={(e) => set('areaId', e.target.value || null)}>
                    <option value="">– wählen –</option>
                    {meta?.areas.filter((a) => a.active).map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Team (optional)" htmlFor="z-team">
                  <Select id="z-team" value={form.teamId ?? ''} onChange={(e) => set('teamId', e.target.value || null)}>
                    <option value="">– kein Team –</option>
                    {meta?.teams.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Bearbeiter (optional, wird per E-Mail informiert)" htmlFor="z-assignee">
                  <Select id="z-assignee" value={form.assigneeId ?? ''} onChange={(e) => set('assigneeId', e.target.value || null)}>
                    <option value="">– niemand –</option>
                    {meta?.agents.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </Select>
                </Field>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <Toggle
                  checked={form.requireCustomer}
                  onChange={(v) => set('requireCustomer', v)}
                  label={form.kind === 'NETDOC' ? 'Nur mit Kunde' : 'Nur Hosts mit Kunde'}
                  hint={form.kind === 'NETDOC' ? 'Vorgänge ohne zuordenbaren Kunden (Kundennummer, Mandant, Name) erzeugen kein Ticket' : 'Hosts ohne Kundenzuordnung erzeugen kein Ticket'}
                />
                <Toggle checked={form.requireContract} onChange={(v) => set('requireContract', v)} label="Nur mit Wartungsvertrag" hint="Nur Kunden mit aktivem Wartungsvertrag (im gewählten Ticketbereich) erhalten Tickets" />
                <Toggle
                  checked={form.resolveOnRecovery}
                  onChange={(v) => set('resolveOnRecovery', v)}
                  label={form.kind === 'NETDOC' ? 'Bei „erledigt“ aus NetDoc lösen' : 'Bei Wiederherstellung lösen'}
                  hint="Sonst nur interne Notiz im Ticket"
                />
                <Toggle checked={form.allowAcknowledge} onChange={(v) => set('allowAcknowledge', v)} label={`Bestätigung an ${KIND_LABEL[form.kind]}`} hint="Mitarbeitende mit Recht „Zabbix-Probleme bestätigen“" />
                {form.kind === 'NETDOC' && (
                  <>
                    <Toggle
                      checked={form.reportTickets}
                      onChange={(v) => set('reportTickets', v)}
                      label="Ticketnummer an NetDoc melden"
                      hint="Nummer und Link am Vorgang; „In Bearbeitung“, sobald ein Bearbeiter zugewiesen ist"
                    />
                    <Toggle
                      checked={form.resolveInSource}
                      onChange={(v) => set('resolveInSource', v)}
                      label="Ticket erledigt → Vorgang erledigt"
                      hint="Gelöste/geschlossene Tickets werden immer an NetDoc gemeldet (tritt das Problem danach erneut auf, entsteht ein neues Ticket). Mit diesem Schalter wird zusätzlich der Vorgang in NetDoc erledigt."
                    />
                  </>
                )}
              </div>
            </fieldset>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Abbrechen
            </Button>
            <Button type="submit" loading={save.isPending}>
              Speichern
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Anleitung für den Medientyp in Zabbix – Geheimnis nur direkt nach dem Erzeugen sichtbar. */
function WebhookDialog({ target, onClose }: { target: { id: string; secret: string | null } | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [secret, setSecret] = useState<string | null>(null);
  const [confirmDialog, confirm] = useConfirm();
  useEffect(() => setSecret(target?.secret ?? null), [target]);
  const { data } = useQuery({
    queryKey: ['integrations', 'zabbix', 'webhook', target?.id],
    queryFn: () => api<WebhookSetup>(`/integrations/zabbix/${target!.id}/webhook`),
    enabled: target !== null,
  });
  const rotate = useMutation({
    mutationFn: () => api<{ webhookSecret: string }>(`/integrations/zabbix/${target!.id}/webhook-secret`, { method: 'POST' }),
    onSuccess: (r) => {
      setSecret(r.webhookSecret);
      qc.invalidateQueries({ queryKey: KEY });
    },
    onError,
  });
  const params = data?.parameters.map((p) => ((p.name === 'secret' || p.name === 'Geheimnis') && secret ? { ...p, value: secret } : p)) ?? [];
  return (
    <Dialog open={target !== null} onOpenChange={(o) => !o && onClose()}>
      {confirmDialog}
      <DialogContent wide>
        <DialogHeader>
          <DialogTitle>Webhook in {data ? KIND_LABEL[data.kind] : '…'} einrichten</DialogTitle>
          <DialogDescription>
            {data?.kind === 'NETDOC'
              ? 'Damit NetDoc Vorgänge sofort meldet. Zusätzlich gleicht das Portal jede Minute ab, denn NetDoc wiederholt fehlgeschlagene Zustellungen nicht.'
              : 'Damit Zabbix Probleme sofort meldet. Ohne Webhook gleicht das Portal aktive Probleme minütlich ab.'}
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="grid gap-4 text-sm">
          {secret ? (
            <Alert variant="success">
              <p className="mb-2 font-medium">Webhook-Geheimnis – jetzt kopieren, es wird nicht erneut angezeigt:</p>
              <CopyRow label="Geheimnis" value={secret} secret />
            </Alert>
          ) : (
            <Alert>
              Das Geheimnis wird nur direkt nach dem Erzeugen angezeigt (beim Anlegen bzw. hier).{' '}
              <button
                type="button"
                className="font-medium text-primary hover:underline"
                onClick={async () =>
                  (await confirm({ title: 'Neues Webhook-Geheimnis erzeugen?', description: `Das bisherige Geheimnis wird sofort ungültig und muss in ${data ? KIND_LABEL[data.kind] : 'der Quelle'} ersetzt werden.`, confirmLabel: 'Neu erzeugen' })) &&
                  rotate.mutate()
                }
              >
                Neues Geheimnis erzeugen
              </button>
            </Alert>
          )}
          {!data ? (
            <Skeleton className="h-40" />
          ) : data.kind === 'NETDOC' ? (
            <>
              <ol className="grid list-decimal gap-2 pl-5">
                <li>
                  In NetDoc unter <em>Einstellungen › Benachrichtigungen</em> auf <strong>+ Empfänger</strong> klicken.
                </li>
                <li>Die Werte unten übernehmen (Adresse und Geheimnis kopieren) und speichern.</li>
                <li>
                  Mit <em>Testen</em> eine Testnachricht schicken – hier erscheint danach unter <em>Letzter Webhook</em> der Zeitpunkt.
                </li>
                <li>
                  Kundennummern: am NetDoc-Mandanten das Feld <em>external_id</em> mit der Kundennummer aus dem Portal füllen – oder hier <em>Kundennummern eintragen</em> einschalten und die Zuordnung im Portal vornehmen.
                </li>
              </ol>
              <CopyRow label="Adresse" value={data.url} />
              <div className="overflow-hidden rounded-md border">
                <table className="w-full text-xs">
                  <tbody>
                    {params.map((p) => (
                      <tr key={p.name} className="border-b last:border-0">
                        <td className="w-36 bg-muted/40 px-2 py-1 font-medium">{p.name}</td>
                        <td className="px-2 py-1">{p.name === 'Geheimnis' && secret ? <code>{secret}</code> : p.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <>
              <ol className="grid list-decimal gap-2 pl-5">
                <li>
                  In Zabbix unter <em>Benachrichtigungen → Medientypen</em> einen Medientyp vom Typ <strong>Webhook</strong> anlegen (Name z. B. „Serviceportal“).
                </li>
                <li>Das Skript unten einfügen und die Parameter in genau dieser Form anlegen.</li>
                <li>
                  Unter <em>Nachrichtenvorlagen</em> Vorlagen für <em>Problem</em>, <em>Problem behoben</em> und <em>Problem aktualisiert</em> hinzufügen (Inhalt beliebig, wird nicht verwendet).
                </li>
                <li>
                  Einem Zabbix-Benutzer (z. B. „serviceportal“) das Medium „Serviceportal“ zuweisen (<em>Senden an</em>: beliebig, z. B. <code>serviceportal</code>).
                </li>
                <li>
                  Unter <em>Benachrichtigungen → Aktionen → Trigger-Aktionen</em> eine Aktion anlegen, die in <em>Operationen</em>, <em>Wiederherstellungsoperationen</em> und <em>Aktualisierungsoperationen</em> an diesen Benutzer über „Serviceportal“ sendet.
                </li>
              </ol>
              <CopyRow label="Webhook-URL" value={data.url} />
              <div className="grid gap-1">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-muted-foreground">Parameter</span>
                  <Button type="button" variant="ghost" size="sm" onClick={() => copy(params.map((p) => `${p.name}\t${p.value}`).join('\n'), 'Parameter')}>
                    <Copy /> Alle kopieren
                  </Button>
                </div>
                <div className="overflow-hidden rounded-md border">
                  <table className="w-full text-xs">
                    <tbody>
                      {params.map((p) => (
                        <tr key={p.name} className="border-b last:border-0">
                          <td className="w-48 bg-muted/40 px-2 py-1 font-mono">{p.name}</td>
                          <td className="truncate px-2 py-1 font-mono">{p.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="grid gap-1">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-muted-foreground">Skript</span>
                  <Button type="button" variant="ghost" size="sm" onClick={() => copy(data.script, 'Skript')}>
                    <Copy /> Kopieren
                  </Button>
                </div>
                <pre className="max-h-56 overflow-auto rounded-md border bg-muted/40 p-3 text-xs">{data.script}</pre>
              </div>
            </>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

/** Zugang von NetDoc zur Portal-API (Kunden, Ticketstufen, Ticketstand) – Token nur direkt nach dem Erzeugen sichtbar. */
function PortalApiSection({ i }: { i: ZabbixInstance }) {
  const qc = useQueryClient();
  const [confirmDialog, confirm] = useConfirm();
  const [created, setCreated] = useState<{ token: string; apiUrl: string } | null>(null);
  const create = useMutation({
    mutationFn: () => api<{ token: string; apiUrl: string }>(`/integrations/zabbix/${i.id}/portal-token`, { method: 'POST' }),
    onSuccess: (r) => {
      setCreated(r);
      qc.invalidateQueries({ queryKey: KEY });
    },
    onError,
  });
  const revoke = useMutation({
    mutationFn: () => api(`/integrations/zabbix/${i.id}/portal-token`, { method: 'DELETE' }),
    onSuccess: () => {
      setCreated(null);
      toast.success('Zugang entzogen');
      qc.invalidateQueries({ queryKey: KEY });
    },
    onError,
  });
  return (
    <div className="grid gap-2 rounded-md border p-3 text-sm">
      {confirmDialog}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="font-medium">Zugang von NetDoc zum Portal</p>
          <p className="text-muted-foreground">
            NetDoc liest Kunden (für die Zuordnung), Ticketstufen und den Stand seiner Tickets.{' '}
            {i.hasPortalToken ? (
              <>Eingerichtet {formatDateTime(i.portalTokenCreatedAt)}, zuletzt genutzt: {i.portalTokenUsedAt ? formatRelative(i.portalTokenUsedAt) : 'noch nie'}.</>
            ) : (
              'Nicht eingerichtet.'
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            loading={create.isPending}
            onClick={async () =>
              (!i.hasPortalToken ||
                (await confirm({ title: 'Neues Token erzeugen?', description: 'Das bisherige Token wird sofort ungültig und muss in NetDoc ersetzt werden.', confirmLabel: 'Neu erzeugen' }))) &&
              create.mutate()
            }
          >
            <KeyRound /> {i.hasPortalToken ? 'Neues Token' : 'Token erzeugen'}
          </Button>
          {i.hasPortalToken && (
            <Button
              size="sm"
              variant="outline"
              className="text-destructive"
              onClick={async () => (await confirm({ title: 'Zugang entziehen?', description: 'NetDoc kann danach nicht mehr auf das Portal zugreifen.', confirmLabel: 'Entziehen', destructive: true })) && revoke.mutate()}
            >
              Entziehen
            </Button>
          )}
        </div>
      </div>
      {created && (
        <Alert variant="success" className="grid gap-2">
          <p className="font-medium">In NetDoc als Ticketsystem-API eintragen – das Token wird nicht erneut angezeigt:</p>
          <CopyRow label="API-Adresse" value={created.apiUrl} />
          <CopyRow label="Token (Authorization: Bearer …)" value={created.token} secret />
        </Alert>
      )}
    </div>
  );
}

function InstanceCard({ i, onEdit, onWebhook }: { i: ZabbixInstance; onEdit: () => void; onWebhook: () => void }) {
  const qc = useQueryClient();
  const [confirmDialog, confirm] = useConfirm();
  const refresh = () => {
    qc.invalidateQueries({ queryKey: KEY });
    qc.invalidateQueries({ queryKey: ['monitoring'] });
  };
  const test = useMutation({ mutationFn: () => api<{ ok: boolean; message: string }>(`/integrations/zabbix/${i.id}/test`, { method: 'POST' }), onSuccess: (r) => (show(r), refresh()), onError });
  const hosts = useMutation({ mutationFn: () => api<{ ok: boolean; message: string }>(`/integrations/zabbix/${i.id}/sync-hosts`, { method: 'POST' }), onSuccess: (r) => (show(r), refresh()), onError });
  const poll = useMutation({ mutationFn: () => api<{ ok: boolean; message: string }>(`/integrations/zabbix/${i.id}/poll`, { method: 'POST' }), onSuccess: (r) => (show(r), refresh()), onError });
  const remove = useMutation({ mutationFn: () => api(`/integrations/zabbix/${i.id}`, { method: 'DELETE' }), onSuccess: () => (toast.success('Instanz entfernt'), refresh()), onError });

  return (
    <Card>
      {confirmDialog}
      <CardHeader className="flex-row items-start justify-between gap-3 space-y-0">
        <div className="min-w-0">
          <CardTitle className="flex flex-wrap items-center gap-2">
            <Server className="size-4 text-muted-foreground" /> {i.name}
            <Badge variant="outline">{KIND_LABEL[i.kind]}</Badge>
            {i.status === 'OK' && <Badge variant="success">Verbunden{i.version ? ` · ${i.version}` : ''}</Badge>}
            {i.status === 'ERROR' && <Badge variant="destructive">Fehler</Badge>}
            {i.status === 'UNTESTED' && <Badge variant="outline">Nicht geprüft</Badge>}
            {!i.active && <Badge variant="outline">inaktiv</Badge>}
          </CardTitle>
          <p className="mt-1 truncate text-sm text-muted-foreground">{i.url}</p>
        </div>
        <div className="flex shrink-0 gap-1">
          <Button size="icon" variant="ghost" className="size-8" aria-label={`${i.name} bearbeiten`} onClick={onEdit}>
            <Pencil />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="size-8 text-destructive hover:text-destructive"
            aria-label={`${i.name} entfernen`}
            onClick={async () =>
              (await confirm({
                title: `„${i.name}“ entfernen?`,
                description: 'Hosts bzw. Endpunkte, Zuordnungen und Ereignisverlauf dieser Quelle werden gelöscht. Bereits erstellte Tickets bleiben erhalten.',
                confirmLabel: 'Entfernen',
                destructive: true,
              })) && remove.mutate()
            }
          >
            <Trash2 />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="grid gap-3">
        {i.lastError && <Alert variant="destructive">{i.lastError}</Alert>}
        <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-[auto_1fr]">
          <dt className="text-muted-foreground">{i.kind === 'NETDOC' ? 'Endpunkte / offene Vorgänge' : 'Hosts / aktive Probleme'}</dt>
          <dd>
            {i.hostCount} / {i.openProblems}
          </dd>
          <dt className="text-muted-foreground">Ticketregel</dt>
          <dd>
            {i.autoTicket ? `automatisch ab „${severityLabel(i.kind, i.minSeverity)}“${i.requireCustomer ? (i.kind === 'NETDOC' ? ', nur mit Kunde' : ', nur Kundenhosts') : ''}${i.requireContract ? ', nur mit Wartungsvertrag' : ''}` : 'keine automatischen Tickets'}
          </dd>
          <dt className="text-muted-foreground">Letzter Webhook</dt>
          <dd>
            {i.lastWebhookAt ? (
              formatRelative(i.lastWebhookAt)
            ) : (
              <span className="text-amber-700 dark:text-amber-300">{i.hasWebhookSecret ? 'noch keiner empfangen' : 'nicht eingerichtet – „Webhook einrichten“'}</span>
            )}
          </dd>
          <dt className="text-muted-foreground">Letzter Abgleich / Hostimport</dt>
          <dd>
            {i.pollEnabled ? formatDateTime(i.lastPollAt) : 'abgeschaltet'} / {formatDateTime(i.lastHostSyncAt)}
          </dd>
        </dl>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => test.mutate()} loading={test.isPending}>
            <CheckCircle2 /> Verbindung testen
          </Button>
          <Button size="sm" variant="outline" onClick={() => hosts.mutate()} loading={hosts.isPending}>
            <Download /> {i.kind === 'NETDOC' ? 'Endpunkte und Standorte importieren' : 'Hosts importieren'}
          </Button>
          <Button size="sm" variant="outline" onClick={() => poll.mutate()} loading={poll.isPending}>
            <RefreshCw /> {i.kind === 'NETDOC' ? 'Vorgänge abgleichen' : 'Probleme abgleichen'}
          </Button>
          <Button size="sm" variant="outline" onClick={onWebhook}>
            <Webhook /> Webhook einrichten
          </Button>
        </div>
        {i.kind === 'NETDOC' && <PortalApiSection i={i} />}
      </CardContent>
    </Card>
  );
}

export function ZabbixPage() {
  const { data, isLoading } = useQuery({ queryKey: KEY, queryFn: () => api<{ items: ZabbixInstance[] }>('/integrations/zabbix') });
  const [edit, setEdit] = useState<{ id: string | null; form: Form } | null>(null);
  const [webhook, setWebhook] = useState<{ id: string; secret: string | null } | null>(null);

  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader
        title="Monitoring & RMM"
        description="Zabbix und NetDoc verbinden – auch beide gleichzeitig. Hosts bzw. Endpunkte importieren und aus Problemen Tickets erzeugen."
        actions={
          <Button onClick={() => setEdit({ id: null, form: empty })}>
            <Plus /> Quelle verbinden
          </Button>
        }
      />
      <div className="grid gap-4">
        {isLoading && <Skeleton className="h-48" />}
        {data?.items.length === 0 && (
          <Card>
            <CardContent className="grid gap-3 py-10 text-center">
              <KeyRound className="mx-auto size-8 text-muted-foreground" />
              <p className="font-medium">Noch keine Monitoring-Quelle verbunden</p>
              <p className="text-sm text-muted-foreground">Sie benötigen die Adresse von Zabbix bzw. NetDoc und ein API-Token.</p>
              <div>
                <Button onClick={() => setEdit({ id: null, form: empty })}>
                  <Plus /> Quelle verbinden
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        {data?.items.map((i) => (
          <InstanceCard
            key={i.id}
            i={i}
            onEdit={() => setEdit({ id: i.id, form: { ...empty, ...i, apiToken: '' } })}
            onWebhook={() => setWebhook({ id: i.id, secret: null })}
          />
        ))}
      </div>
      <InstanceDialog edit={edit} onClose={() => setEdit(null)} onCreated={(id, secret) => setWebhook({ id, secret })} />
      <WebhookDialog target={webhook} onClose={() => setWebhook(null)} />
    </div>
  );
}
